import { useEffect, useState } from "react";

/**
 * Track which of a known set of section `id`s is currently "active"
 * (i.e. the user's reading position).
 *
 * Backed by a single IntersectionObserver:
 *
 *  - The `rootMargin` carves out a horizontal band roughly aligned with
 *    where the eye lands when reading — the top 35% and bottom 60% of
 *    the viewport are treated as "out of focus". This means a section
 *    is only marked active once its heading has risen into the upper
 *    third of the screen, which matches how Apple, Stripe, and modern
 *    docs sites pick a TOC entry.
 *  - We maintain a `Map<id, boolean>` of which targets are currently
 *    intersecting the focus band, then pick the topmost intersecting
 *    target by DOM order — keeping the active state stable while a
 *    long section dominates the viewport, and flipping cleanly when
 *    the next heading reaches the band.
 *  - When the user is above the first section (e.g. the page hero) we
 *    return `null`, so the consumer can choose to highlight nothing or
 *    default to the first entry as appropriate.
 *
 * The hook is read-only — it doesn't manipulate the URL hash, because
 * doing so on scroll fights with the "smooth scroll on TOC click"
 * behaviour. Callers can write the hash themselves if they want.
 */
export function useActiveHeading(
  sectionIds: readonly string[],
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sectionIds.length === 0) {
      setActiveId(null);
      return;
    }

    /* Build a stable ordering of targets by current DOM position so we
     * can pick the *topmost* intersecting one each time the observer
     * fires. We re-query inside the effect so HMR / dynamic content
     * additions don't leave us holding stale references. */
    const targets = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (targets.length === 0) return;

    /* Order targets by their position in the document so we can pick
     * the topmost intersecting one without re-querying the DOM each
     * time the observer fires. */
    const orderedIds = targets
      .map((el) => ({ id: el.id, top: el.getBoundingClientRect().top }))
      .sort((a, b) => a.top - b.top)
      .map((t) => t.id);

    const intersecting = new Map<string, boolean>(
      orderedIds.map((id) => [id, false]),
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          intersecting.set(entry.target.id, entry.isIntersecting);
        }
        const topActive = orderedIds.find((id) => intersecting.get(id));
        setActiveId(topActive ?? null);
      },
      {
        rootMargin: "-35% 0px -60% 0px",
        threshold: 0,
      },
    );

    for (const target of targets) {
      observer.observe(target);
    }

    return () => {
      observer.disconnect();
    };
    // Re-run when the set of ids changes. Joining them keeps the
    // dependency stable as long as the underlying ids are stable.
  }, [sectionIds.join("|")]);

  return activeId;
}
